import { makeObservable, observable, action, runInAction } from 'mobx';
import { IGroup, NO_GROUP_ID } from '@api/Groups.api';
import { RootStore } from './Root.store';
import { groupsAPI } from '@api/Groups.api';
import { devicesAPI, IDeviceDescription } from '@api/Devices.api';
import LanguageStore from './Language.store';

export interface IChangeDevice extends Partial<IDeviceDescription> {}

/**
 * Корзина «без группы» приходит с сервера с русским именем — на клиенте оно
 * подменяется словарным, чтобы список папок был на языке интерфейса.
 */
export const localizeNoGroup = (groups: IGroup[], lang: LanguageStore): IGroup[] =>
    groups.map((group) =>
        group.id === NO_GROUP_ID
            ? { ...group, name: lang.getWord('default') }
            : group
    );

export default class GroupsStore {
    @observable
    public defaultIcon = 1;

    @observable
    public groupsList: IGroup[];

    @observable
    public operationSuccess: boolean;

    @observable
    public loading: boolean;

    @observable
    public serverError: boolean;

    @observable
    public selectedGroupId: string | null;

    @observable
    public createGroupLoading: boolean;

    @observable
    public changeGroupDescriptionLoading: boolean;

    @observable
    public deleteGroupLoading: boolean;

    @observable
    public addDeviceLoading: boolean;

    constructor(private rootStore: RootStore) {
        this.setDefault();
        makeObservable(this);
    }

    @action
    setDefault() {
        this.groupsList = [];
        this.loading = false;
        this.serverError = false;
        this.operationSuccess = false;
        this.selectedGroupId = null;
        this.createGroupLoading = false;
        this.changeGroupDescriptionLoading = false;
        this.deleteGroupLoading = false;
        this.addDeviceLoading = false;
    }

    @action
    async getLength(groupId: string) {
        try {
            const token = this.rootStore.user.token;
            const limit = 1;
            const res = await devicesAPI.getGroupDevices({
                token,
                groupId,
                limit,
            } as any);
            if (res.result === 'success') {
                const length = res.total;
                return length;
            } else {
                runInAction(() => {
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.serverError = true;
            });
        }
    }

    @action
    async getUserGroups() {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.loading = true;
            this.groupsList = [];
            const res = await groupsAPI.getUserGroups(token);
            if (res.result === 'success') {
                runInAction(() => {
                    this.groupsList = localizeNoGroup(
                        res.groups,
                        this.rootStore.lang
                    );
                });                
                await this.rootStore.devices.getGroupsDevices(); //здесь идет уже вызов устройст в группах ?!
                runInAction(() => {
                    this.loading = false;
                });
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async createGroup(name: string, userIcon?: number) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            this.createGroupLoading = true;
            const icon = userIcon !== undefined ? userIcon : this.defaultIcon;
            const res = await groupsAPI.createGroup(token, name, icon);
            if (res.result === 'success') {
                runInAction(() => {
                    this.createGroupLoading = false;
                    this.operationSuccess = true;
                    this.getUserGroups();
                });
            } else {
                runInAction(() => {
                    this.createGroupLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.createGroupLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async createGroupWithoutLoading(name: string, userIcon?: number) {
        const token = this.rootStore.user.token;
        if (!token) return;
        try {
            const icon = userIcon !== undefined ? userIcon : this.defaultIcon;
            const res = await groupsAPI.createGroup(token, name, icon);
            if (res.result === 'success') {
                runInAction(() => {
                    this.loading = false;
                    this.operationSuccess = true;
                });
            } else {
                runInAction(() => {
                    this.loading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.loading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async changeGroupDescription({ name, icon: userIcon }: IChangeDevice) {
        const token = this.rootStore.user.token;
        const groupId = this.selectedGroupId;
        if (!groupId || !token || !name) return;
        try {
            this.changeGroupDescriptionLoading = true;
            const icon = userIcon !== undefined ? userIcon : this.defaultIcon;
            const description = { name, icon };
            const res = await groupsAPI.changeGroupDescription(
                token,
                groupId,
                description,
            );
            if (res.result === 'success') {
                runInAction(() => {
                    this.changeGroupDescriptionLoading = false;
                    this.operationSuccess = true;
                    this.getUserGroups();
                });
            } else {
                runInAction(() => {
                    this.changeGroupDescriptionLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.changeGroupDescriptionLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async deleteGroup() {
        const groupId = this.selectedGroupId;
        const token = this.rootStore.user.token;
        if (!groupId || !token) return;
        try {
            this.deleteGroupLoading = true;
            const res = await groupsAPI.deleteGroup(token, groupId);
            if (res.result === 'success') {
                runInAction(() => {
                    this.deleteGroupLoading = false;
                    this.operationSuccess = true;
                    this.getUserGroups();
                });
            } else {
                runInAction(() => {
                    this.deleteGroupLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.deleteGroupLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    async addDevice(groupId: string) {
        const token = this.rootStore.user.token;
        const imei = this.rootStore.devices.editedDeviceImei;
        if (!groupId || !imei || !token) return;
        try {
            this.addDeviceLoading = true;
            const res = await groupsAPI.addDevice(token, imei, groupId);
            if (res.result === 'success') {
                await this.rootStore.devices.getGroupsDevices();
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.operationSuccess = true;
                    const groupId = this.rootStore.map.selectedGroupId;
                    if (
                        this.rootStore.devices.deviceList.filter(
                            (d) => groupId === d.groupId && d.imei === imei,
                        ).length > 0
                    ) {
                        this.rootStore.map.addSelectedDevices(imei);
                    } else this.rootStore.map.removeSelectedDevices(imei);
                });
            } else {
                runInAction(() => {
                    this.addDeviceLoading = false;
                    this.serverError = true;
                });
            }
        } catch (e) {
            runInAction(() => {
                this.addDeviceLoading = false;
                this.serverError = true;
            });
        }
    }

    @action
    setSelectedGroupId(id: string) {
        this.selectedGroupId = id;
    }

    @action
    resetSelectedGroupId() {
        this.selectedGroupId = null;
    }

    @action
    resetServerError() {
        this.serverError = false;
    }

    @action
    resetOperationSuccess() {
        this.operationSuccess = false;
    }

    @action
    resetGroups() {
        this.groupsList = [];
    }
}
